import { useCallback, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './ui.css';

export { default as Image } from './Image';
export { default as Dialog } from './Dialog';

/* ─────────────────────────── Section header ─────────────────────────── */

export const SectionHeader = ({ title, link, linkTo, linkHref, sub, id }) => (
  <>
    <div className="section-head">
      <h2 className="section-head__title" id={id}>{title}</h2>
      {link && linkTo && (
        <Link className="section-head__link" to={linkTo}>{link}</Link>
      )}
      {link && linkHref && (
        <a
          className="section-head__link"
          href={linkHref}
          target="_blank"
          rel="noopener noreferrer"
        >
          {link}
        </a>
      )}
    </div>
    {sub && <p className="section-head__sub">{sub}</p>}
  </>
);

/* ────────────────────────────── Tag ────────────────────────────── */

export const Tag = ({ children, variant = '', lang }) => (
  <span className={`tag ${variant && `tag--${variant}`}`} lang={lang}>
    {children}
  </span>
);

/* ──────────────────────────── Stat row ──────────────────────────── */

export const StatRow = ({ stats, boxed = false }) => (
  <dl className={`stats ${boxed ? 'stats--boxed' : ''}`}>
    {stats.filter(Boolean).map((s, i) => (
      <div className="stat" key={`${s.label}-${i}`}>
        <dd className="stat__value">{s.value}</dd>
        <dt className="stat__label">{s.label}</dt>
      </div>
    ))}
  </dl>
);

/* ──────────────────────────── Timeline ──────────────────────────── */

export const Timeline = ({ items }) => (
  <div className="timeline">
    {items.map((item, i) => (
      <article className={`tl-item ${item.current ? 'tl-item--current' : ''}`} key={i}>
        <div className="tl-date">{item.date}</div>
        <div className="tl-body">
          <div className="tl-head">
            <h3 className="tl-title">{item.title}</h3>
            {item.type && (
              <span className={`tl-type tl-type--${item.type}`}>{item.type}</span>
            )}
          </div>
          {item.org && (
            <p className="tl-org">
              {item.org}{item.location ? ` · ${item.location}` : ''}
            </p>
          )}
          {item.description && <p className="tl-desc">{item.description}</p>}
        </div>
      </article>
    ))}
  </div>
);

/* ────────────────────────────── Tabs ──────────────────────────────
   A real tablist: roving selection, arrow-key navigation, and panels
   wired by id. The old log tabs were plain buttons with none of this. */

export const Tabs = ({ tabs, active, onChange, label }) => {
  const refs = useRef([]);

  const onKeyDown = (event) => {
    const i = tabs.findIndex((t) => t.id === active);
    let next = null;
    if (event.key === 'ArrowRight') next = (i + 1) % tabs.length;
    if (event.key === 'ArrowLeft') next = (i - 1 + tabs.length) % tabs.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = tabs.length - 1;
    if (next === null) return;
    event.preventDefault();
    onChange(tabs[next].id);
    refs.current[next]?.focus();
  };

  return (
    <div className="tabs" role="tablist" aria-label={label} onKeyDown={onKeyDown}>
      {tabs.map((tab, i) => (
        <button
          key={tab.id}
          ref={(el) => { refs.current[i] = el; }}
          type="button"
          role="tab"
          id={`tab-${tab.id}`}
          className="tab"
          aria-selected={active === tab.id}
          aria-controls={`panel-${tab.id}`}
          tabIndex={active === tab.id ? 0 : -1}
          onClick={() => onChange(tab.id)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
};

export const TabPanel = ({ id, children }) => (
  <div role="tabpanel" id={`panel-${id}`} aria-labelledby={`tab-${id}`} tabIndex={-1}>
    {children}
  </div>
);

/* ──────────────────────── Dialog trigger helper ────────────────────────
   Keeps the element that opened a dialog so focus can be returned to it,
   which is the half of the pattern callers usually forget. */

export const useDialog = () => {
  const triggerRef = useRef(null);
  const [payload, setPayload] = useState(null);

  const open = useCallback((value, event) => {
    if (event?.currentTarget) triggerRef.current = event.currentTarget;
    setPayload(value);
  }, []);

  const close = useCallback(() => {
    setPayload(null);
    triggerRef.current?.focus();
  }, []);

  return { payload, isOpen: payload !== null, open, close };
};
